import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import { setCookie, getCookieByName } from '@helpers/session'
import { analytics } from '@helpers/analytics'
import { getIsFirstTimeUser } from '@helpers/session/user'
import { COOKIE_EXPIRE_1_MONTH } from '@consts/index'
import { PageComponent } from './helper/pageComponent'
import styles from './styles.module.scss'

export const ShoppableCards = dynamic(
  () => import('../components/ShoppableCardsSection')
)
export const StatementSection = dynamic(
  () => import('../components/StatementSection')
)
export const RichTextSection = dynamic(
  () => import('../components/RichTextSection')
)
export const Benefits = dynamic(() => import('@components/ui/Benefits'))
export const ShoppableCardsSection = dynamic(
  () => import('../components/ShoppableCardsSection')
)
export const ProductsBreakdown = dynamic(
  () => import('@components/ui/ProductsBreakdown')
)
export const LandingPageProductsCards = dynamic(
  () => import('../components/LandingPageProductsCards')
)
export const HeroBanner = dynamic(() => import('../components/HeroBanner'))
export const HeroVideoBanner = dynamic(
  () => import('../components/HeroVideoBanner')
)
export const HeroBannerAnimated = dynamic(
  () => import('../components/HeroBannerAnimated')
)
export const Reviews = dynamic(() => import('../components/Reviews'))
export const PageHeader = dynamic(() => import('../components/PageHeader'))
export const FeatureCards = dynamic(() => import('../components/FeatureCards'))
export const SectionHeadline = dynamic(
  () => import('../components/SectionHeadline')
)
export const IconCards = dynamic(() => import('../components/IconCards'))
export const ImageCopySection = dynamic(
  () => import('../components/ImageCopySection')
)
export const SizingWidget = dynamic(
  () => import('@components/product/SizingWidget'),
  { ssr: false }
)
export const ComparisonCards = dynamic(
  () => import('../components/ComparisonCards')
)

const mapToComponent = {
  imageCarousel: HeroBanner,
  shoppableCards: ShoppableCards,
  heroVideoBanner: HeroVideoBanner,
  heroBannerAnimated: HeroBannerAnimated,
  statementSection: StatementSection,
  richTextSection: RichTextSection,
  benefits: Benefits,
  productBreakdown: ProductsBreakdown,
  landingPageProductCards: LandingPageProductsCards,
  reviews: Reviews,
  pageHeader: PageHeader,
  featureCards: FeatureCards,
  pageHeadline: SectionHeadline,
  iconCards: IconCards,
  imageCopySection: ImageCopySection,
  sizingWidget: SizingWidget,
  comparisonCards: ComparisonCards
} as const

export type PageProps = {
  type?: string
  pageType?: string
} & React.ComponentProps<(typeof mapToComponent)[keyof typeof mapToComponent]>

export type DynamicPagesProps = {
  handle: string
  page: PageProps[]
}

const DynamicPages = ({ handle, page }: DynamicPagesProps) => {
  const dispatch = useDispatch()
  const router = useRouter()
  const { fte } = router.query
  const firstTimeUser = getIsFirstTimeUser()
  const filteredPageData =
    (page || []).filter(
      pageData =>
        !!mapToComponent[pageData?.type as keyof typeof mapToComponent]
    ) || []
  const pageType = (page || []).find(pageData => pageData?.pageType)?.pageType

  useEffect(() => {
    if (fte) {
      analytics('First Time Visitor triggered by campaign', {
        name: 'First Time Visitor triggered by campaign',
        label: 'First Time Visitor triggered by campaign'
      })
    } else if (!getCookieByName('th_LV')) {
      analytics('First Time Visitor Viewed', {
        name: 'First Time Visitor Viewed',
        label: 'First Time Visitor Viewed'
      })
      window.sessionStorage.setItem('th_ftv', 'true')
    }
    setCookie('th_LV', `${Date.now()}`, COOKIE_EXPIRE_1_MONTH)
  }, [fte, dispatch])

  useEffect(() => {
    if (handle === '/new-user') {
      analytics('New User Homepage Viewed', {
        category: 'Homepage',
        label: 'New User Homepage Viewed'
      })
    }

    if (handle === '/') {
      analytics('Existing User Homepage Viewed', {
        category: 'Homepage',
        label: 'Existing User Homepage Viewed'
      })
    }
  }, [handle])

  return (
    <div className={styles.pageWrapper} data-page-loaded>
      {filteredPageData.map((pageData, index) => {
        const nextPageData = filteredPageData[index + 1]
        return (
          <PageComponent
            key={index}
            index={index}
            pageData={pageData}
            nextPageData={nextPageData}
            rootPath={/^\/(new-user)?$/.test(handle)}
            firstTimeUser={firstTimeUser}
            pageType={pageType}
          />
        )
      })}
    </div>
  )
}

export default DynamicPages
